import { html, property } from 'lit-element';
import {
  event,
  EventEmitter,
  KatLitElement,
  register,
} from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './currency-selector.lit.scss';
import getString from './strings';

/**
 * PLEASE NOTE
 * The currencies are duplicated for support in TypeScript and the docs site.
 */

interface Currency {
  id: string;
  symbol: string;
}

/**
 * Valid supported currencies.
 * @typedef {Object} SupportedCurrency
 * @type {object}
 * @property {string} AED United Arab Emirates Dirham
 * @property {string} AUD Australian Dollar
 * @property {string} BGN Bulgarian Lev
 * @property {string} BRL Brazilian Real
 * @property {string} CAD Canadian Dollar
 * @property {string} CHF Swiss Franc
 * @property {string} CLP Chilean Peso
 * @property {string} CNY Chinese Yuan
 * @property {string} COP Colombian Peso
 * @property {string} DKK Danish Krone
 * @property {string} EGP Egyptian Pound
 * @property {string} EUR Euro
 * @property {string} GBP Pound Sterling
 * @property {string} HKD Hong Kong Dollar
 * @property {string} ILS Israeli Shekel
 * @property {string} INR Indian Rupee
 * @property {string} JPY Japanese Yen
 * @property {string} KRW South Korean Won
 * @property {string} MXN Mexican Peso
 * @property {string} MYR Malaysian Ringgit
 * @property {string} NGN Nigerian Naira
 * @property {string} NZD New Zealand Dollar
 * @property {string} PKR Pakistani Rupee
 * @property {string} PLN Polish Złoty
 * @property {string} RON Romanian Leu
 * @property {string} SAR Saudi Riyal
 * @property {string} SEK Swedish Krona
 * @property {string} SGD Singapore Dollar
 * @property {string} THB Thai Baht
 * @property {string} TRY Turkish Lira
 * @property {string} TWD New Taiwan Dollar
 * @property {string} UAH Ukrainian Hryvnia
 * @property {string} USD United States Dollars
 * @property {string} VND Vietnamese Dong
 * @property {string} ZAR South African Rand
 */
export const SUPPORTED_CURRENCIES: Currency[] = [
  { id: 'AED', symbol: 'د.إ' },
  { id: 'AUD', symbol: '$' },
  { id: 'BGN', symbol: 'Лв.' },
  { id: 'BRL', symbol: 'R$' },
  { id: 'CAD', symbol: '$' },
  { id: 'CHF', symbol: '₣' },
  { id: 'CLP', symbol: '$' },
  { id: 'CNY', symbol: '¥' },
  { id: 'COP', symbol: '$' },
  { id: 'DKK', symbol: 'Kr.' },
  { id: 'EGP', symbol: 'E£' },
  { id: 'EUR', symbol: '€' },
  { id: 'GBP', symbol: '£' },
  { id: 'HKD', symbol: 'HK$' },
  { id: 'ILS', symbol: '₪' },
  { id: 'INR', symbol: '₹' },
  { id: 'JPY', symbol: '¥' },
  { id: 'KRW', symbol: '₩' },
  { id: 'MXN', symbol: '$' },
  { id: 'MYR', symbol: 'RM' },
  { id: 'NGN', symbol: '₦' },
  { id: 'NZD', symbol: '$' },
  { id: 'PKR', symbol: 'Rs' },
  { id: 'PLN', symbol: 'zł' },
  { id: 'RON', symbol: 'lei' },
  { id: 'SAR', symbol: 'ريال' },
  { id: 'SEK', symbol: 'kr' },
  { id: 'SGD', symbol: 'S$' },
  { id: 'THB', symbol: '฿' },
  { id: 'TRY', symbol: '₺' },
  { id: 'TWD', symbol: 'NT$' },
  { id: 'UAH', symbol: '₴' },
  { id: 'USD', symbol: '$' },
  { id: 'VND', symbol: '₫' },
  { id: 'ZAR', symbol: 'R' },
];

// Creates type that enforces array must be made of strings from IDs above.
type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number]['id'];

/**
 * @component {kat-currency-selector} KatCurrencySelector The Currency Selector provides a standard method to allow your end user to select the currency in your product. This component only provides a selection method - it’s up to you to manage the conversions themselves. The currency list is based on the supported currencies for Seller Central and will be updated to match if this changes in the future.
 * @status Production
 * @theme flo
 * @example Basic {"value":"JPY"}
 * @example UsingInclude {"include": "[\"GBP\",\"INR\",\"USD\"]"}
 * @example UsingExclude {"exclude": "[\"AED\",\"AUD\"]"}
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-currency-selector')
export class KatCurrencySelector extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /** The value of the selected currency. */
  @property({ reflect: true })
  value?: string;

  /** Array of currency IDs to include.  Unsupported currency IDs are ignored. Cannot be combined with exclude. Defaults to all supported currency IDs. */
  @property({ reflect: true })
  include?: SupportedCurrency[];

  /** Array of currency IDs to exclude.  Unsupported currency IDs are ignored. Cannot be combined with include. Defaults to []. */
  @property({ reflect: true })
  exclude?: SupportedCurrency[];

  /**
   * Locale to identify the language used in fixed labels.
   * Use this property/attribute to override the automatic language detection.
   * @type {string}
   */
  @property({ reflect: true })
  locale?: string;

  /** Setting disabled stops interactivity. */
  @property({ reflect: true })
  disabled?: boolean;

  /**
   * Setting the state of the input changes its look to give the user more information about what they have entered. This value must be set for State Labels to show up.
   * @enum {value} error Lets the user know there is a problem with the value they have entered in the input.
   */
  @property()
  state?: string;

  /** Optional pretext for the state label. Provides additional context to the state for the input. */
  @property({ attribute: 'state-emphasis' })
  stateEmphasis?: string;

  /** Provides users with more information about why the input is in the state it is in. */
  @property({ attribute: 'state-label' })
  stateLabel?: string;

  /** Whether the component is currently showing its dropdown options. */
  @property()
  expanded?: boolean;

  /**
   * The max height of the selection area.
   * This should be a css value with units, e.g. "8px".
   */
  @property({ attribute: 'max-height' })
  maxHeight?: string;

  /** Emits after the region-selector value has changed */
  @event('change', true)
  private _change: EventEmitter<{ value: string; values?: string[] }>;

  /** Emits whenever the region-selector is expanded. */
  @event('expand')
  private _expand: EventEmitter;

  /** Emits whenever the region-selector is collapsed. */
  @event('collapse')
  private _collapse: EventEmitter;

  constructor() {
    super();
    this.updateAvailableCurrencies();
  }

  // Updates when the options are filtered OR a value is set
  shouldUpdate(changedProperties: Map<string, any>): boolean {
    const hasIncludeOrExclude =
      changedProperties.has('include') || changedProperties.has('exclude');

    if (hasIncludeOrExclude || changedProperties.has('locale')) {
      this.updateAvailableCurrencies();
    }

    // Stop an invalid value from being set - value has already changed, we need to reset
    if (changedProperties.has('value') && this.isValueInvalid()) {
      this.value = changedProperties.get('value');
    }

    return true;
  }

  // Clears value if updated include/exclude make it invalid
  updated(changedProperties: Map<string, any>) {
    if (
      (changedProperties.has('include') || changedProperties.has('exclude')) &&
      this.isValueInvalid()
    ) {
      this.value = '';
    }
  }

  private isValueInvalid(): boolean {
    if (!this.value) {
      return false;
    }

    return !this.availableCurrencies.some(({ id }) => id === this.value);
  }

  private handleChange(e: { detail: { value?: string; values?: string[] } }) {
    this.value = e.detail.value;
    this._change.emit({ value: this.value });
  }

  private handleExpand() {
    this._expand.emit();
  }

  private handleCollapse() {
    this._collapse.emit();
  }

  // Up-to-date array of countries, honors include/exclude properties
  private availableCurrencies: Currency[];

  private updateAvailableCurrencies() {
    const hasInclude = this.include?.length > 0;
    const hasExclude = this.exclude?.length > 0;
    let currencies = SUPPORTED_CURRENCIES;

    if (hasInclude && hasExclude) {
      console.warn(
        'Using both `include` and `exclude` attributes is not supported.'
      );
    }

    if (hasInclude) {
      currencies = SUPPORTED_CURRENCIES.filter(currency =>
        this.include.includes(currency.id)
      );
    } else if (hasExclude) {
      currencies = SUPPORTED_CURRENCIES.filter(
        currency => !this.exclude.includes(currency.id)
      );
    }

    this.availableCurrencies = currencies;
  }

  render() {
    const currencies = this.availableCurrencies.map(({ id, symbol }) => {
      // Casting as any necessary since we dynamically build the key
      const localizedName = getString(
        `currency-selector-${id}` as any,
        undefined,
        this.locale
      );

      return html`<kat-option value=${id}
        >${symbol} - ${id} - ${localizedName}</kat-option
      >`;
    });

    return html`
      <kat-dropdown
        rich-selection-label
        searchable
        ?disabled=${this.disabled}
        ?expanded=${this.expanded}
        .locale=${this.locale}
        .value=${this.value}
        .state=${this.state}
        .stateEmphasis=${this.stateEmphasis}
        .stateLabel=${this.stateLabel}
        .maxHeight=${this.maxHeight}
        @change=${this.handleChange}
        @collapse=${this.handleCollapse}
        @expand=${this.handleExpand}
      >
        <div>${currencies}</div>
        <div></div>
      </kat-dropdown>
    `;
  }
}
